import type {
  ProductOptionCreateInput,
  ProductOptionUpdateInput,
} from "@/modules/options/domain/contracts";
import { Button, FormInput, FormRoot, RHF, zodResolver } from "@devhop/ui";
import type { Resolver, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import type { ProductOptionDTO } from "../api/client";
import { GripVertical, PlusIcon, TrashIcon } from "lucide-react";
import { useCallback, useEffect } from "react";

const OptionValueSchema = z.object({
  valueId: z.string().optional(),
  valueLo: z.string().min(1, "ຕ້ອງໃສ່ຊື່ (ລາວ)"),
  valueTh: z.string().optional(),
  valueEn: z.string().optional(),
  sortOrder: z.coerce.number().int().min(0).default(0),
});

const OptionFormSchema = z.object({
  nameLo: z.string().min(1, "ຕ້ອງໃສ່ຊື່ (ລາວ)"),
  nameTh: z.string().optional(),
  nameEn: z.string().optional(),
  slug: z.string().min(1, "ຕ້ອງໃສ່ Slug"),
  sortOrder: z.coerce.number().int().min(0).default(0),
  values: z.array(OptionValueSchema).default([]),
});

export type OptionFormValues = z.output<typeof OptionFormSchema>;

const DEV_FILL: OptionFormValues = {
  nameLo: "ສີ",
  nameTh: "สี",
  nameEn: "Color",
  slug: "color",
  sortOrder: 0,
  values: [
    {
      valueId: undefined,
      valueLo: "ແດງ",
      valueTh: "แดง",
      valueEn: "Red",
      sortOrder: 0,
    },
    {
      valueId: undefined,
      valueLo: "ຟ້າ",
      valueTh: "ฟ้า",
      valueEn: "Blue",
      sortOrder: 1,
    },
    {
      valueId: undefined,
      valueLo: "ຂຽວ",
      valueTh: "เขียว",
      valueEn: "Green",
      sortOrder: 2,
    },
  ],
};

type OptionFormProps = {
  mode: "create" | "edit";
  option?: ProductOptionDTO;
  onSubmit: (
    values: ProductOptionCreateInput | ProductOptionUpdateInput
  ) => void;
  onCancel?: () => void;
  submitting?: boolean;
  onDevFill?: () => void;
};

export function OptionForm({
  mode,
  option,
  onSubmit,
  onCancel,
  submitting,
  onDevFill,
}: OptionFormProps) {
  const defaultValues: OptionFormValues = {
    nameLo: (option?.name as { lo?: string })?.lo ?? "",
    nameTh: (option?.name as { th?: string })?.th ?? "",
    nameEn: (option?.name as { en?: string })?.en ?? "",
    slug: option?.slug ?? "",
    sortOrder: option?.sortOrder ?? 0,
    values:
      option?.values?.map((v) => ({
        valueId: v.id,
        valueLo: (v.value as { lo?: string })?.lo ?? "",
        valueTh: (v.value as { th?: string })?.th ?? "",
        valueEn: (v.value as { en?: string })?.en ?? "",
        sortOrder: v.sortOrder,
      })) ?? [],
  };

  const methods = RHF.useForm<OptionFormValues>({
    resolver: zodResolver(OptionFormSchema) as Resolver<OptionFormValues>,
    defaultValues,
  });

  const { fields, append, remove, move } = RHF.useFieldArray({
    control: methods.control,
    name: "values",
  });

  // Auto-generate slug from English name (create mode or when slug not set)
  const generateSlug = useCallback((text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }, []);

  const nameEn = methods.watch("nameEn");
  useEffect(() => {
    if (nameEn && !option?.slug) {
      const slug = generateSlug(nameEn);
      if (slug && slug !== methods.getValues("slug")) {
        methods.setValue("slug", slug);
      }
    }
  }, [nameEn, generateSlug, methods, option?.slug]);

  const handleAddValue = () => {
    append({
      valueId: undefined,
      valueLo: "",
      valueTh: "",
      valueEn: "",
      sortOrder: fields.length,
    });
  };

  const fillDev = () => {
    methods.reset(DEV_FILL);
  };

  const handleSubmit = (vals: OptionFormValues) => {
    if (mode === "create") {
      const payload: ProductOptionCreateInput = {
        name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
        slug: vals.slug,
        sortOrder: vals.sortOrder,
        values: vals.values.map((v, index) => ({
          value: { lo: v.valueLo, th: v.valueTh, en: v.valueEn },
          sortOrder: v.sortOrder ?? index,
        })),
      };
      onSubmit(payload);
    } else {
      const payload: ProductOptionUpdateInput = {
        name: { lo: vals.nameLo, th: vals.nameTh, en: vals.nameEn },
        slug: vals.slug,
        sortOrder: vals.sortOrder,
        values: vals.values.map((v, index) => ({
          id: v.valueId,
          value: { lo: v.valueLo, th: v.valueTh, en: v.valueEn },
          sortOrder: v.sortOrder ?? index,
        })),
      };
      onSubmit(payload);
    }
  };

  return (
    <FormRoot<OptionFormValues>
      methods={methods as UseFormReturn<OptionFormValues>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6" data-tourid="catalog-option-form">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium">ຂໍ້ມູນ Option</h3>
            {onDevFill && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={fillDev}
              >
                ເຕີມຂໍ້ມູນທົດສອບ
              </Button>
            )}
          </div>
          <div
            className="grid grid-cols-1 gap-4 md:grid-cols-3"
            data-tourid="catalog-option-name"
          >
            <FormInput
              name="nameLo"
              label="ຊື່ Option (ລາວ)"
              placeholder="ຕົວຢ່າງ: ສີ"
              required
            />
            <FormInput
              name="nameTh"
              label="ຊື່ Option (ໄທ)"
              placeholder="ตัวอย่าง: สี"
            />
            <FormInput
              name="nameEn"
              label="ຊື່ Option (EN)"
              placeholder="e.g. Color"
            />
          </div>
          <div
            className="grid grid-cols-1 gap-4 md:grid-cols-2"
            data-tourid="catalog-option-slug"
          >
            <FormInput name="slug" label="Slug" placeholder="color" required />
            <FormInput
              name="sortOrder"
              label="ລຳດັບການສະແດງ"
              type="number"
              min={0}
            />
          </div>
        </div>

        <div className="space-y-4" data-tourid="catalog-option-values">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Option Values</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddValue}
            >
              <PlusIcon className="mr-1 h-4 w-4" />
              ເພີ່ມຄ່າ Option
            </Button>
          </div>

          {fields.length === 0 && (
            <p className="text-muted-foreground text-sm">
              ຍັງບໍ່ມີຄ່າ option. ໃຫ້ກົດ &quot;ເພີ່ມຄ່າ Option&quot;
              ເພື່ອເພີ່ມຄ່າ ເຊັ່ນ Red, Blue ຫຼື S, M, L.
            </p>
          )}

          <div className="space-y-3">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="flex flex-col gap-3 rounded-lg border bg-card px-3 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">
                      Value {index + 1}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {index > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => move(index, index - 1)}
                        aria-label="Move up"
                      >
                        ↑
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(index)}
                      aria-label="Remove value"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <FormInput
                    name={`values.${index}.valueLo`}
                    label="ຊື່ຄ່າ (ລາວ)"
                    placeholder="ຕົວຢ່າງ: ແດງ"
                    required
                  />
                  <FormInput
                    name={`values.${index}.valueTh`}
                    label="ຊື່ຄ່າ (ໄທ)"
                    placeholder="ตัวอย่าง: แดง"
                  />
                  <FormInput
                    name={`values.${index}.valueEn`}
                    label="ຊື່ຄ່າ (EN)"
                    placeholder="e.g. Red"
                  />
                </div>

                <div className="max-w-xs">
                  <FormInput
                    name={`values.${index}.sortOrder`}
                    label="ລຳດັບ"
                    type="number"
                    min={0}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 pt-2" data-tourid="catalog-option-submit">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel ?? (() => methods.reset(defaultValues))}
          >
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
